<template>
  <v-container>
    <v-alert v-if="warning" type="warning" class="mt-5" border="left" colored-border>
      <i18n path="warning_namespace">
        <a place="here" :href="docsWarn" target="_blank">{{ $t('here') }}</a>
      </i18n>
    </v-alert>
    <div class="headline">
      {{ $t('title') }}
    </div>
    <v-row v-if="params.deploymentMethod.password && currentTab === 'compose'">
      <v-col>
        <v-text-field
          class="mb-4"
          :value="adminPassword"
          :label="$t('admin_password.label')"
          :hint="$t('admin_password.hint')"
          :append-icon="showPassword ? 'visibility_off' : 'visibility'"
          @click:append="() => (showPassword = !showPassword)"
          :type="showPassword ? 'text' : 'password'"
          persistent-hint
          readonly
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-tabs
          class="commandTabs"
          dark
        >
          <v-tab
            v-for="tab in deployTabs"
            :key="tab.id"
            @change="updateTab(tab.id)"
          >
            {{ tab.title }}
          </v-tab>
          <v-tab-item class="command" key="vApp">
            <kbd v-if="!pending">
              <div>
                <a :href="vAppDownloadURL" target="_blank">{{ $t('vApp.download') }}</a>
                <p>{{ vApp }}</p>
              </div>
            </kbd>
            <div class="pending" v-else>
              <v-progress-circular
                :size="70"
                :width="7"
                color="secondary"
                indeterminate
              ></v-progress-circular>
            </div>
          </v-tab-item>
          <v-tab-item class="command" key="compose">
            <kbd>
              <template v-if="!pending">{{ dockerCompose }}</template>
              <template v-else class="pending">
                <v-progress-circular
                  :size="70"
                  :width="7"
                  color="secondary"
                  indeterminate
                ></v-progress-circular>
              </template>
            </kbd>
            <v-btn depressed class="clipboard" v-clipboard:copy="dockerCompose" large>
              <v-icon>content_copy</v-icon>
            </v-btn>
          </v-tab-item>
          <v-tab-item class="command" key="kubernetes">
            <kbd>{{ kubernetes }}</kbd>
            <v-btn depressed class="clipboard" v-clipboard:copy="kubernetes" large>
              <v-icon>content_copy</v-icon>
            </v-btn>
          </v-tab-item>
        </v-tabs>
      </v-col>
    </v-row>
    <v-btn text @click="$emit('cancel')">{{ $t('wizard.cancel') }}</v-btn>
    <v-btn text @click="$emit('prev')">{{ $t('wizard.prev') }}</v-btn>
    <v-btn color="primary" @click="$emit('next')">{{ $t('complete') }}</v-btn>
  </v-container>
</template>

<script>
import NetworkService from '@/services/network'
import DeployInfoComponent from '@/components/deploy/Info.vue'
import DeployPanelComponent from '@/components/deploy/Panel.vue'
import NamespaceComponent from '@/components/deploy/Namespace.vue'
import { generateDockerCompose } from '@/services/compose'
import { generateKubernetesCommand } from '@/services/kubernetes'
import { docsUrl } from '@/services/docs'
import { tokenBodies, tokenFindRules, TokenUser } from '@/model/token'

export default {
  data () {
    return {
      operatingSystem: '',
      token: '',
      vaultToken: '',
      adminPassword: '',
      showPassword: true,
      deploymentNameRules: [
        (d) => !!d || this.$t('error.deploymentName_required'),
        (d) => this.validateDeploymentName(d) || this.$t('error.deploymentName_invalid')
      ],
      currentTab: 'vapp',
      deployTabs: [
        { id: 'vApp', title: this.$t('vApp.title') },
        { id: 'compose', title: this.$t('docker-compose.title') },
        { id: 'kubernetes', title:  this.$t('kubernetes.title') }
      ],
      tabs: {
        info: { title: this.$t('tabs.info.title'), text: this.$t('tabs.info.text') },
        namespaces: { title: this.$t('tabs.namespaces.title') },
        detected: { title: this.$t('tabs.detected.title'), text: this.$t('tabs.detected.text') }
      },
      pending: false,
      docs: docsUrl('faq'),
      docsWarn: docsUrl('quickstart'),
      warning: false,
      vAppAIODownloadURL: process.env.VUE_APP_VMWARE_AIO_APP_URL,
      vAppCollectorDownloadURL: process.env.VUE_APP_VMWARE_COLLECTOR_APP_URL
    }
  },
  props: {
    params: Object
  },
  computed: {
    vApp () {
      return [
        `${this.$t('vApp.info')}`,
        `${this.$t('vApp.token')} ${this.token.token}`,
        `${this.$t('vApp.vaultToken')} ${this.vaultToken.token}`
      ].join('\n')
    },
    vAppDownloadURL () {
      if (this.params.deploymentMethod.id === 'collector') return this.vAppCollectorDownloadURL
      return this.vAppAIODownloadURL
    },
    kubernetes () {
      const deploymentMethod = this.params.deploymentMethod.id
      return generateKubernetesCommand(deploymentMethod, this.token.token, this.vaultToken.token)
    }
  },
  asyncComputed: {
    dockerCompose () {
      this.adminPassword = this.params.adminPassword
      const deploymentMethod = this.params.deploymentMethod.id
      const uuid = this.params.uuid
      return generateDockerCompose(deploymentMethod, this.token.token, this.vaultToken.token, uuid, this.deploymentName, this.adminPassword)
    }
  },
  created () {
    this.fetchDevicesInterval = setInterval(this.fetchAddedDevices, 5000)
    this.fetchTokens(this.params.deployment)
  },
  destroyed () {
    clearInterval(this.fetchDevicesInterval)
  },
  methods: {
    fetchTokens (namespace) {
      return Promise.all([
        this.fetchToken(namespace, TokenUser.NODE),
        this.fetchToken(namespace, TokenUser.VAULT)]
      ).then((tokens) => {
        this.token = tokens[0]
        this.vaultToken = tokens[1]
      })
    },
    fetchToken (namespace, user) {
      this.pending = true
      return NetworkService.namespaceTokens(namespace).then((data) => {
        const token = data.find(tokenFindRules[user])
        if (token !== undefined) {
          return token
        } else {
          const body = tokenBodies[user]
          return NetworkService.createToken(namespace, body)
        }
      }).catch(() => {
        this.namespaceExist = false
      }).finally(() => {
        this.pending = false
      })
    },
    fetchAddedDevices () {
      const filter = this.params.uuid
      NetworkService.nodes(this.namespace).then((data) => {
        this.listAddedDevices = data.filter((device) => {
          return device.id.includes(filter)
        })
      })
    },
    updateTab (tab) {
      this.currentTab = tab
    }
  },
  components: {
    'u-deploy-info': DeployInfoComponent,
    'u-deploy-panel': DeployPanelComponent,
    'u-namespace-combobox': NamespaceComponent
  }
}
</script>

<style  lang="scss" scoped>
.commandTabs {
  .v-tab {
    text-transform: none !important;
  }
}

.command {
  display: flex;
  flex-flow: row nowrap;

  kbd {
    border-radius: 0;
    word-break: break-all;
    flex: 1 1 100%;
    max-height: 14rem;
    min-height: 7rem;
    padding: 1rem;
    overflow: auto;
    white-space: pre;

    a {
      color: white;
    }

    p {
      margin-bottom: 0px !important;
    }
  }

  .clipboard {
    flex: 0 0 auto;
    height: auto;
    margin: 0;
    min-width: 0;
    padding: 0 .5rem;
  }
}
</style>

<i18n>
{
  "en": {
    "title": "Choose your deployment method",
    "side_title": "Deploy",
    "installation": "installation",
    "docker_placeholder": "Please fill in the fields to generate the docker command ...",
    "v_app_placeholder": "Please fill in the fields to generate the vApp download link ...",
    "display_name": "{displayName}:",
    "connected": "connected",
    "warning_namespace": "Please note that there is already an infrastructure deployed in this namespace. Make sure that this is the behaviour that you seek. You can follow the deployment steps {here}.",
    "here": "here",
    "os": {
      "unix": "Unix",
      "windows": "Windows"
    },
    "id": {
      "label": "Enter the deployment name",
      "hint": "This will allow you to identify your nodes later on. Make sure your deployment name is memorable and meaningful."
    },
    "admin_password": {
      "label": "Initial Unryo Monitor Portal password",
      "hint": "On a new deployment, you can authenticate into the Unryo Monitor Portal as \"admin\" using this password. It doesn't have any effect during an upgrade."
    },
    "docker": {
      "title": "Docker",
      "label": "Run the following command on your host :",
      "namespace_warning": "The namespace that you have selected does not exist yet. Please create the namespace with the button above."
    },
    "docker-compose": {
      "title": "Docker-Compose"
    },
    "kubernetes": {
      "title": "Kubernetes"
    },
    "vApp": {
      "title": "VMware vCenter vApp",
      "download": "Download Unryo Monitor vApp",
      "info": "Information you will need at installation:",
      "token": "Unryo Token:",
      "vaultToken": "Vault Token:"
    },
    "trial": "Not available in trial version.",
    "error": {
      "deploymentName_required": "The deployment name is required.",
      "deploymentName_invalid": "It must be at most 64 characters long and only contain letters, numbers and dashes."
    },
    "tabs": {
      "info": {
        "title": "Info",
        "compose": {
          "title": "For Docker-Compose:",
          "points": {
            "copy": "Copy the content into a file inside your host named \"docker-compose.yml\".",
            "up": "Start your containers with the following command: \"docker-compose up -d\""
          },
          "doc": {
            "url": "quickstart",
            "id": "docker"
          }
        },
        "vApp": {
          "title": "For VMware vCenter vApp:",
          "points": {
            "click": "Click the provided link to download the vApp image."
          },
          "doc": {
            "url": "quickstart",
            "id": "vmware-vapp"
          }
        },
        "kubernetes": {
          "title": "For Kubernetes:",
          "doc": {
            "url": "quickstart",
            "id": "kubernetes-helm"
          }
        },
        "faq": "See {faq} FAQs"
      },
      "namespaces": {
        "title": "Namespaces",
        "text": "You have {numberOfDevice} components currently on the {namespace} namespace."
      },
      "detected": {
        "title": "Newly Detected",
        "text": "We will let you know when new components will start communicating with the Unryo Service …"
      }
    },
    "complete": "I'm done installing"
  }
}
</i18n>
